import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router';

import { AppRoutes, LOGIN_INPUT_CLASSES, LOGIN_SUBMIT_BUTTON_CLASSES } from '@/constants';
import { useInitiatePasswordMutation } from '@/hooks';
import { navigate } from '@/providers';

import { Loading02Icon } from '@/assets/icons';
import { AuthGlassContainer, Button, Input } from '@/components/common';
import { Alert } from '@/lib/ui';

import { SETUP_PASSWORD_FORM_FIELDS, SETUP_PASSWORD_SCHEMA } from '@/features/auth/constants';
import { ISetupPasswordPayload } from '@/features/auth/interfaces';
import { isValidPassword } from '@/features/auth/utils';

import { InvalidInvitation } from './InvalidInvitation';
import { PasswordConstraints } from './PasswordConstraints';

type PasswordField = 'password' | 'confirmPassword';
type Status = 'success' | 'error' | undefined;

export const InitialPassword = () => {
  const [searchParams] = useSearchParams();
  const encryptedEmail = searchParams.get('email') ?? '';
  const otp = searchParams.get('otp') ?? '';

  const { mutateAsync: setupPasswordMutation, isPending } = useInitiatePasswordMutation();
  const [focusedField, setFocusedField] = useState<PasswordField | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ISetupPasswordPayload>({
    resolver: yupResolver(SETUP_PASSWORD_SCHEMA),
    mode: 'onTouched',
  });

  const passwordValue = watch(SETUP_PASSWORD_FORM_FIELDS.password, '');
  const confirmPasswordValue = watch(SETUP_PASSWORD_FORM_FIELDS.confirmPassword, '');
  const passwordsMismatch = Boolean(confirmPasswordValue) && passwordValue !== confirmPasswordValue;

  const isFocused = (field: PasswordField) => focusedField === field;

  const onFocus = (field: PasswordField) => () => setFocusedField(field);
  const onBlur = (field: PasswordField) => () =>
    setFocusedField((prev) => (prev === field ? null : prev));

  const showPasswordStatus = !isFocused('password') && Boolean(passwordValue);
  const showConfirmStatus = !isFocused('confirmPassword') && Boolean(confirmPasswordValue);

  const passwordStatus: Status = !showPasswordStatus
    ? undefined
    : isValidPassword(passwordValue)
    ? 'success'
    : 'error';

  const confirmPasswordStatus: Status = !showConfirmStatus
    ? undefined
    : passwordsMismatch
    ? 'error'
    : 'success';

  const onSubmit = async ({ password }: ISetupPasswordPayload) => {
    await setupPasswordMutation(
      { encryptedEmail, otp, password },
      {
        onSuccess: () => {
          Alert.success('Your password has been set successfully.', 'Please login to access AURA.');
          navigate(AppRoutes.LOGIN);
        },
        onError: () => {
          setShowErrorModal(true);
        },
      },
    );
  };

  if (!encryptedEmail || !otp || showErrorModal) {
    return <InvalidInvitation />;
  }

  return (
    <AuthGlassContainer className="w-[402px] flex flex-col items-center justify-center gap-8">
      <div className="flex flex-col gap-6 w-full">
        <span className="text-h6 text-neutral-0 font-bold uppercase text-center">
          SET UP PASSWORD
        </span>
        <div className="flex flex-col gap-5">
          <div className="flex flex-col">
            <Input
              className={LOGIN_INPUT_CLASSES}
              label="New Password"
              required
              type="password"
              placeholder="Enter password"
              error={errors[SETUP_PASSWORD_FORM_FIELDS.password]?.message}
              {...register(SETUP_PASSWORD_FORM_FIELDS.password, {
                onBlur: onBlur('password'),
              })}
              onFocus={onFocus('password')}
              status={passwordStatus}
              hasSuccessIcon={showPasswordStatus && isValidPassword(passwordValue)}
            />
            <PasswordConstraints password={passwordValue} isFocused={isFocused('password')} />
          </div>
          <Input
            className={LOGIN_INPUT_CLASSES}
            label="Confirm New Password"
            required
            type="password"
            placeholder="Enter confirm password"
            error={
              errors[SETUP_PASSWORD_FORM_FIELDS.confirmPassword]?.message ??
              (passwordsMismatch ? 'Passwords do not match' : undefined)
            }
            status={confirmPasswordStatus}
            hasSuccessIcon={confirmPasswordStatus === 'success'}
            {...register(SETUP_PASSWORD_FORM_FIELDS.confirmPassword, {
              onBlur: onBlur('confirmPassword'),
            })}
            onFocus={onFocus('confirmPassword')}
          />
        </div>
      </div>
      <Button
        type="button"
        buttonColor="primary"
        buttonStyle="filled"
        size="lg"
        className={LOGIN_SUBMIT_BUTTON_CLASSES}
        onClick={handleSubmit(onSubmit)}
        disabled={
          isPending ||
          !watch(SETUP_PASSWORD_FORM_FIELDS.password) ||
          !watch(SETUP_PASSWORD_FORM_FIELDS.confirmPassword) ||
          passwordsMismatch ||
          !isValidPassword(passwordValue)
        }
      >
        {isPending ? (
          <Loading02Icon className="size-6 text-opacity-light-30 animate-[spin_2s_linear_infinite]" />
        ) : (
          'Confirm'
        )}
      </Button>
    </AuthGlassContainer>
  );
};
